<template>

  <section class="rta-decision">

    <!-- The client's Step 4 (UX spec 2.5 item 4, M-24): the approved option without Decline…, then the decision read-only. No lock line, no e-mail button,
         no bottom button. -->
    <template v-if="isClient">
      <rta-option-summary
        heading="Approved option"
        :rows="optionRows"
        :option-id="optionIdForView"
        :option-no="approvedOptionNo" />

      <el-form label-position="left" label-width="132px" class="rta-decision__client" @submit.native.prevent>
        <el-form-item label="Decision">
          <h2 class="rta-decision__heading">{{ clientText.heading }}&nbsp;<i :class="'fa ' + clientText.icon + ' ' + clientText.tone" aria-hidden="true"></i></h2>
          <i v-if="clientText.when">{{ clientText.when }}</i>
        </el-form-item>
      </el-form>

      <read-only-remarks v-if="clientText.remarks" label="Decision Remarks" :html="clientText.remarks" />

      <p v-if="clientText.line" class="rta-decision__client-line">
        <small class="text-muted"><i v-if="clientText.locked" class="fa fa-lock" aria-hidden="true"></i><span v-if="clientText.locked">&nbsp;</span>{{ clientText.line }}</small>
      </p>
    </template>

    <!-- The Tower Co's Step 4 (UX spec 2.5): the approved option with Decline…, then the decision (Pending or Approved), the approval e-mail and the lock line. -->
    <template v-else>

      <rta-option-summary
        heading="Approved option"
        :rows="optionRows"
        :option-id="optionIdForView"
        :option-no="approvedOptionNo">
        <!-- Decline… (decision 142): small, the closer only, while the RTA is in Client Approved. It asks for a reason first. -->
        <el-button
          v-if="canDecline"
          ref="declineButton"
          type="danger"
          size="mini"
          plain
          round
          icon="fa fa-times-circle"
          :aria-label="'Decline option ' + approvedOptionNo"
          :disabled="declining || lockBusy !== null"
          @click="startDecline">Decline…</el-button>
      </rta-option-summary>

      <!-- The lock line is the Decision section's (UX spec 2.5 item 2, mock M-21a): it sits right under the approved option, above the Decision title. -->
      <approval-lock-line
        ref="lockLine"
        placement="end"
        :row="lockRow"
        :title="'RTA #' + rta.id"
        :decided="storedDecided"
        :frozen="terminal"
        :can-unlock="canUnlock"
        :busy="lockBusy !== null"
        :busy-label="lockBusyText"
        :format="formatDateTime"
        text-id="rta-decision-lock-line"
        @unlock="$emit('unlock')" />

      <h5 class="rta-decision__title">{{ names.towerCo }} Decision</h5>

      <el-form label-position="top" :inline="true" class="rta-decision__form" @submit.native.prevent>

        <el-form-item label="Decision">
          <template v-if="editable">
            <!-- Element's el-select puts no attribute of ours on its input: the name and the notes it points at are set on the
                 input itself (applySelectAttrs), where a screen reader reads them. -->
            <el-select
              ref="statusSelect"
              :value="form.status"
              placeholder="Select"
              class="rta-decision__select"
              @change="onStatus">
              <el-option label="Pending" value="Pending" />
              <el-option label="Approved" value="Approved" :disabled="approvedDisabled" />
            </el-select>
            <div v-if="showApprovedNote"><small id="rta-decision-approved-note" class="text-muted rta-note"><i class="fa fa-lock" aria-hidden="true"></i>&nbsp;{{ approvedNoteText }}</small></div>
            <div v-if="rebindText"><small id="rta-decision-rebind-note" class="text-muted rta-note">{{ rebindText }}</small></div>
          </template>
          <template v-else>
            <b>{{ form.status }}</b>
            <div v-if="gate"><small id="rta-decision-gate-note" class="text-muted rta-note"><i class="fa fa-lock" aria-hidden="true"></i>&nbsp;{{ gateText }}</small></div>
          </template>
        </el-form-item>

        <el-form-item v-if="form.status === 'Approved'" label="Option">
          <b>{{ optionLine }}</b>
        </el-form-item>

        <date-input
          v-if="decided"
          label="Date"
          :value="form.date"
          :disabled="!editable"
          @input="onDate" />

        <!-- Decision 191 (Carlos, 2026-10-29: "Always show remarks wysiwyg text input."): whoever may decide always has the editor, Pending included (the API keeps
             Pending's remarks, DEV-14). A read-only viewer sees the remarks once there is a decision, as before. -->
        <el-form-item v-if="editable || decided" class="rta-decision__full">
          <h5 class="remarks-heading">Decision Remarks</h5>
          <template v-if="editable">
            <!-- XOL-67 B10: a Tower Co client reads nothing and is e-mailed nothing (decision 14): the note would say both. -->
            <el-alert
              v-if="!tcClient"
              type="warning"
              title="Please Note"
              show-icon
              :closable="false"
              :description="pleaseNote" />
            <html-input :value="form.remarks" no-form @input="onRemarks" />
          </template>
          <read-only-remarks v-else aria-label="Decision Remarks" :html="form.remarks" />
        </el-form-item>

        <!-- Decision 55: the e-mail button works exactly like RTS step 2's (step2.vue:217-238): the tooltip on the wrapper while
             Send E-mails is off (a disabled button fires no mouse events), then the last-sent line, then the Re-send picker. -->
        <el-form-item v-if="emailView.visible && !readOnly" class="rta-decision__full rta-decision__email">
          <el-tooltip :disabled="!mailOff" :tabindex="mailOff ? 0 : -1" :content="mailOffTitle" placement="top" effect="dark">
            <span class="approval-send-wrap" :class="{ 'is-mail-off': mailOff }" :tabindex="mailOff ? 0 : -1">
              <el-button
                :type="buttonType"
                :loading="approvalMailSending"
                :disabled="!emailView.enabled || approvalMailSending"
                :aria-label="sendText"
                @click="sendDecisionEmail">{{ sendText }}</el-button>
            </span>
          </el-tooltip>
        </el-form-item>

        <el-form-item v-if="shownMail" class="rta-decision__full">
          <approval-mail-line :mail="shownMail" :action-data="rta" />
        </el-form-item>

      </el-form>

      <resend-recipients-dialog
        v-if="emailView.visible && !readOnly"
        v-bind="resendDialogProps()"
        @send="submitResend"
        @retry="retryResendPreview"
        @close="closeResendPicker" />

      <!-- Decline… (decision 142, E9; UX spec 4.6): the reason is required. It lives in the one container the dialog needs (a dialog moves its element to <body>). -->
      <div class="rta-decision__dialogs">
        <rta-dialog
          v-if="declineOpen"
          key="decline-dialog"
          :visible="true"
          :icon="declineCopy.icon"
          :title="declineCopy.title"
          :subtitle="declineCopy.subtitle"
          :ok-text="declineCopy.okText"
          :ok-type="declineCopy.okType"
          :loading="declining"
          focus-selector=".rta-decline-field textarea"
          @confirm="doDecline"
          @cancel="cancelDecline">

          <p v-for="(line, i) in declineCopy.paragraphs" :key="i">{{ line }}</p>

          <el-form label-position="top" @submit.native.prevent>
            <text-input
              class="rta-decline-field"
              type="textarea"
              required
              :label="declineCopy.reasonLabel"
              :rows="4"
              :value="declineReason"
              :error="declineError || null"
              :validate-event="false"
              @input="onDeclineReason" />
          </el-form>
        </rta-dialog>
      </div>

    </template>

  </section>

</template>


<script>

/*
 * XOL-67 (B5, rewritten in V5 for v2.1): Step 4, Tower Co Decision (plan 4 V5, UX spec 2.5, decisions 41, 45, 49, 55, 107, 142). It mirrors step5.vue's
 * Tower Co branch, built from the atoms because an RTA has no step rows: ApprovalLockLine, the status select, the date, the
 * remarks editor, and the Send / Re-send Approved e-Mail button of RTS step 2.
 *
 *   - The approved option on top (RtaOptionSummary), read from the options list by `approvedOptionId`. The TC closer's small Decline… (E9,
 *     POST /candidate/{id}/decline { reason }) sits beside View option; the reason is required. The server returns the RTA to Client Review, or
 *     to Searching when no option is left in review, and says `declined` to the drawer, which follows to that step.
 *   - The decision is Approve only: Pending or Approved. Status, date and remarks AUTO-SAVE together: one POST /actions/{id}/rta/decision per
 *     pause (lib/rtaAutosave), with `expectedApprovedStatus` = the decision the user saw, `X-Edit-Session` (DEV-17) and the C-12 card. No mail on save.
 *   - The e-mail button (Approved only, C11) flushes first (the XOL-582 ordering rule), then sends `rta_decision` to everyone, or opens the
 *     Re-send picker once it has been sent.
 *   - There is no Lock button: Submit decision (the drawer's footer, RtaDecisionAction) locks, and Unlock (decision 107) is offered on the lock
 *     line while the RTA is in Finalize. Unlock is the drawer's command (RtaDetails), because it also flushes Step 5; this component draws the
 *     line and says `unlock`.
 *   - A Telco reads the approved option and the decision, nothing more.
 *
 * Every gate is lib/rtaStage over `rtaData`, wrapped in `computed`, so it follows the store as it hydrates. A refusal goes through lib/rtaRefusal
 * (`wrong_phase`, `response_changed`, `decision_locked`, `stale_decision`, `not_approved`, `reason_required`).
 */

import axios from '@/lib/axiosClient'
import RequestMixins from '@/mixins/RequestMixins'
import RtaCommandMixins from '@/mixins/RtaCommandMixins'

import * as rtaStage from '@/lib/rtaStage'
import * as rtaDecision from '@/lib/rtaDecision'
import { createAutosave } from '@/lib/rtaAutosave'
import { keepalivePost } from '@/lib/keepalive'
import { copyText } from '@/lib/requestSyncCopy'
import { approvalMailLine } from '@/lib/requestActivity'
import { decisionDay } from '@/lib/requestRowSync'
import { appNowDateSql } from '@/lib/appTime'
import { isTowerCoOrg } from '@/lib/contactRoles'
import { structureLabel } from '@/lib/rtaCompletion'

import RtaDialog from '@/components/requests/acquire/RtaDialog'
import RtaOptionSummary from '@/components/requests/acquire/RtaOptionSummary'
import ApprovalLockLine from '@/components/requests/ApprovalLockLine'
import ApprovalMailLine from '@/components/requests/ApprovalMailLine'
import ResendRecipientsDialog from '@/components/requests/ResendRecipientsDialog'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'
import HtmlInput from '@/components/atoms/inputs/HtmlInput'
import DateInput from '@/components/atoms/inputs/DateInput'
import TextInput from '@/components/atoms/inputs/TextInput'

// A refusal that means "what you are looking at is not what the server holds": the pending edit is dropped and the server's
// decision is shown (the toast said why). Any other 409 (use_transition included) reads again through the notice's `reread`.
var STALE = ['stale_decision', 'decision_locked', 'request_closed', 'invalid_state', 'client_not_approved', 'response_changed', 'wrong_phase']

// XOL-67 B10: while the decision e-mail is still being sent ("Sending"), the RTA is read again now and then until it has settled, so the
// line changes to Sent (or Partly failed, Failed) without closing and reopening the drawer. 20 reads, 3 seconds apart, then it stops.
var MAIL_POLL_MS = 3000
var MAIL_POLL_MAX = 20

export default {
  name: 'RtaDecision',

  mixins: [RequestMixins, RtaCommandMixins],

  components: {
    RtaDialog,
    RtaOptionSummary,
    ApprovalLockLine,
    ApprovalMailLine,
    ResendRecipientsDialog,
    ReadOnlyRemarks,
    HtmlInput,
    DateInput,
    TextInput
  },

  props: {

    // The action (GET /actions/{id}, the RTA shape): the object the drawer holds, so a command's answer shows here at once.
    rtaData: {
      type: Object,
      required: true
    },

    // No edits, no e-mail button (a record seen from elsewhere).
    readOnly: {
      type: Boolean,
      default: false
    },

    // The drawer's lock command in flight: 'lock' | 'unlock' | null (named apart from RequestMixins' own `lockRunning`).
    lockBusy: {
      type: String,
      default: null
    }

  },


  data () {
    return {
      // What is on screen: the Tower Co's own edits, ahead of the server while a save waits.
      form: { status: 'Pending', date: null, remarks: '' },

      // The user picked the date (so it is sent even with a status change); else the server stamps today.
      dateTouched: false,

      // The decision the user saw when they began editing: 'Approved' | null (Pending). `expectedApprovedStatus`.
      base: null,

      // The auto-save's observable state (pending / saving / error).
      autosaveState: null,

      // The RTA's options (the list rows), for the approved option's block, "Option n: title" and the rebinding note.
      options: [],

      // Decline…: the dialog is open, its command is running, the reason typed and the message under it.
      declineOpen: false,
      declining: false,
      declineReason: '',
      declineError: ''
    }
  },


  computed: {

    // RequestMixins reads `actionData`: here it is the drawer's object.
    actionData () {
      return this.rtaData
    },

    rta () {
      return this.rtaData
    },

    viewerOrg () {
      return this.org()
    },

    viewerProfile () {
      return this.profile()
    },

    isClient () {
      return !this.isTowerCo()
    },

    names () {
      var c = this.rta.client || this.rta.organisation || null

      return {
        telco: (c && (c.name || c.shortName)) || 'the client',
        towerCo: this.towerCoName()
      }
    },

    // Is the RTA's client a Tower Co (its own client, decision 14: no RTS, no e-mail)?
    tcClient () {
      var c = this.rta.client || this.rta.organisation || null

      return !!c && isTowerCoOrg(c.category)
    },

    ctx () {
      return {
        telco: this.names.telco,
        towerCo: this.names.towerCo,
        fmtDate: this.formatDate,
        fmtDateTime: this.formatDateTime
      }
    },

    stored () {
      return rtaStage.tcDecision(this.rta)
    },

    storedStatus () {
      return this.stored.approvedStatus || 'Pending'
    },

    storedDecided () {
      return rtaStage.isDecided(this.rta)
    },

    // The raw `state.rta.tc` (the lock line reads approvalLocked, _by and _at; the mail button its decisionMail).
    tcRaw () {
      var s = this.rta && this.rta.state

      return s && s.rta && s.rta.tc && typeof s.rta.tc === 'object' ? s.rta.tc : {}
    },

    lockRow () {
      return this.tcRaw
    },

    // The server's view of the decision as one string: when it changes underneath, the form follows unless an edit is waiting.
    storedKey () {
      return [this.stored.approvedStatus, this.stored.approved_at, this.stored.remarks, this.stored.approvalLocked].join('|')
    },

    locked () {
      return rtaStage.isDecisionLocked(this.rta)
    },

    terminal () {
      return rtaStage.isTerminal(this.rta)
    },

    editable () {
      return !this.readOnly && rtaStage.canDecide(this.rta, this.viewerOrg, this.viewerProfile)
    },

    // A Tower Co user who cannot change the decision because of their ROLE (not because it is locked or closed).
    gate () {
      return !this.terminal && this.isTowerCo() && !rtaStage.isTcCloser(this.rta, this.viewerOrg, this.viewerProfile)
    },

    gateText () {
      return rtaDecision.gateNote(this.names)
    },

    decided () {
      return this.form.status !== 'Pending'
    },

    approvedNoteText () {
      return rtaDecision.approvedNote(this.rta, this.names)
    },

    approvedDisabled () {
      return !!this.approvedNoteText
    },

    // Approved is the choice being denied, so the note is only said while it is not already the value.
    showApprovedNote () {
      return !!this.approvedNoteText && this.form.status !== 'Approved'
    },

    pleaseNote () {
      return rtaDecision.remarksPleaseNote(this.names)
    },

    // The approved option's row: the one the RTA names (`approvedOptionId`, the summary), else the one the decision binds, else the one that is Approved (a Finalize RTA
    // keeps it Approved) or Final Approved (a completed RTA). Null while the options are not read, or when none of them is.
    boundOption () {
      var ids = [rtaStage.optionSummary(this.rta).approvedOptionId, this.stored.optionId]
      var list = this.options
      var found = null

      ids.forEach(function (id) {
        if (found || id === null || id === undefined) {
          return
        }

        list.forEach(function (o) {
          if (!found && Number(o.id) === Number(id)) {
            found = o
          }
        })
      })

      list.forEach(function (o) {
        if (!found && (o.optionStatus === 'Approved' || o.optionStatus === 'Final Approved')) {
          found = o
        }
      })

      return found
    },

    approvedOptionNo () {
      var s = rtaStage.optionSummary(this.rta)

      return this.boundOption ? Number(this.boundOption.optionNo) : (s.approvedOptionNo || s.finalOptionNo || null)
    },

    optionLine () {
      var o = this.boundOption
      var n = this.approvedOptionNo

      if (!n) {
        return 'The approved option'
      }

      return 'Option ' + n + (o && o.title ? ': ' + o.title : '')
    },

    // The id View option opens: the row's, else the one the RTA names; none while neither is known.
    optionIdForView () {
      var id = this.boundOption ? this.boundOption.id : rtaStage.optionSummary(this.rta).approvedOptionId

      return id === null || id === undefined ? null : Number(id)
    },

    // The rows of the approved option's block (UX spec 2.5 item 1).
    optionRows () {
      var o = this.boundOption
      var self = this

      return rtaDecision.approvedOptionRows(o, {
        structure: o ? structureLabel(self.getConfig().structures, o.structure_id) : '',
        telco: this.names.telco,
        towerCo: this.names.towerCo,
        client: this.isClient,
        fmtDate: this.formatDate,
        optionNo: this.approvedOptionNo
      })
    },

    // Decline… (decision 142, I-4): the TC closer, the RTA in Client Approved, the option the submitted Approved one. Not in a read-only drawer.
    canDecline () {
      return !this.readOnly && rtaStage.canDeclineApproved(this.boundOption, this.rta, this.viewerOrg, this.viewerProfile)
    },

    // Where the RTA goes if the approved option is declined (lib/rtaStage.stageAfterApprovalLeaves): Client Review while another option is in Review, else Searching.
    declineToStage () {
      return rtaStage.stageAfterApprovalLeaves(this.rta, 'Declined')
    },

    // The option that is still in Review (the lowest number), when the options are read: the dialog names it.
    stillInReviewNo () {
      var no = null

      this.options.forEach(function (o) {
        if (o.optionStatus === 'Review' && (no === null || Number(o.optionNo) < no)) {
          no = Number(o.optionNo)
        }
      })

      return no
    },

    // Does an e-mail go to the client (a Telco client, Send E-mails on)? Said in the Decline dialog.
    mailOn () {
      return rtaStage.mailOn(this.rta)
    },

    declineCopy () {
      return rtaDecision.declineConfirm({
        optionNo: this.approvedOptionNo,
        toStage: this.declineToStage,
        stillInReviewNo: this.stillInReviewNo,
        name: rtaStage.rtaTitle(this.rta),
        reference: rtaStage.referenceOf(this.rta),
        telco: this.names.telco,
        towerCo: this.names.towerCo,
        tcClient: this.tcClient,
        mailPossible: !this.tcClient && this.mailOn,
        mailOff: !this.tcClient && !this.mailOn
      })
    },

    // Step 5 holds entries saved for another option than the one Approved would bind (UX v3 section 4).
    rebindText () {
      var completion = this.rta.completion

      if (!this.editable || this.storedStatus === 'Approved' || !completion || completion.optionId === null || completion.optionId === undefined || !rtaDecision.keptEntries(this.rta)) {
        return ''
      }

      var approved = null
      var saved = null

      this.options.forEach(function (o) {
        if (o.optionStatus === 'Approved') {
          approved = o
        }

        if (Number(o.id) === Number(completion.optionId)) {
          saved = o
        }
      })

      if (approved && saved && Number(approved.id) === Number(saved.id)) {
        return ''
      }

      if (!approved && !saved) {
        return ''
      }

      return rtaDecision.rebindNote(approved ? Number(approved.optionNo) : null, saved ? Number(saved.optionNo) : null)
    },

    // The RTA as the form would make it: the e-mail button follows what is on screen (it flushes first), the stored decision
    // is what the server judges.
    draftRta () {
      return rtaDecision.withDraft(this.rta, { status: this.form.status, remarks: this.form.remarks })
    },

    emailView () {
      return rtaStage.emailButton(this.draftRta, this.viewerOrg, this.viewerProfile)
    },

    mailOff () {
      return this.requestMailOff()
    },

    mailOffTitle () {
      return this.approvalMailOffTitle()
    },

    decisionMail () {
      return this.stored.decisionMail
    },

    // Does the form say something other than the stored decision? Then the last-sent batch is not of this content: the server nulls
    // `decisionMail` the moment the save lands, and the screen does the same the moment the user edits (decision 55: an edit returns the
    // button to "Send {status} e-Mail" and clears the line).
    formDiffers () {
      var tc = this.stored
      var date = this.form.status === 'Pending' ? null : (tc.approved_at ? decisionDay(tc.approved_at) : this.form.date)

      return !rtaDecision.sameDecision(
        { status: this.form.status, date: this.form.date, remarks: this.form.remarks },
        { status: tc.approvedStatus || 'Pending', date: date, remarks: tc.remarks || '' }
      )
    },

    // The last-sent batch as the screen shows it: none while the form differs from what was sent.
    shownMail () {
      return this.formDiffers ? null : (this.decisionMail || null)
    },

    // Is the batch on screen still going out? Judged by the line itself (its tag says "Sending"), so the two cannot disagree.
    mailInFlight () {
      return !!this.shownMail && approvalMailLine(this.shownMail, {}).tag.key === 'sending'
    },

    // The decision the button is about: the form's (what the user sees decided), with the last-sent batch of that content. With none,
    // pressing it flushes and sends to everyone in one click, never the picker.
    mailRow () {
      return { approvedStatus: this.form.status === 'Pending' ? null : this.form.status, decisionMail: this.shownMail }
    },

    sendText () {
      return this.approvalSendLabel(this.mailRow, 'rta_decision')
    },

    buttonType () {
      return this.form.status === 'Approved' ? 'success' : 'info'
    },

    // There is no separate Lock (decision 142): Submit decision, the drawer's footer, locks an approval, so the lock line only ever offers Unlock, and
    // only in Finalize (decision 107; rtaStage.canUnlockDecision).
    canUnlock () {
      return !this.readOnly && rtaStage.canUnlockDecision(this.rta, this.viewerOrg, this.viewerProfile)
    },

    lockBusyText () {
      return this.lockBusy === 'unlock' ? copyText('requestSync.approvalLock.line.unlocking') : copyText('requestSync.approvalLock.line.locking')
    },

    clientText () {
      return rtaDecision.clientView(this.rta, this.ctx)
    },

    // The ids of the notes under the select, for its aria-describedby.
    selectDescribedBy () {
      var ids = []

      if (this.showApprovedNote) {
        ids.push('rta-decision-approved-note')
      }

      if (this.rebindText) {
        ids.push('rta-decision-rebind-note')
      }

      return ids.length ? ids.join(' ') : null
    },

    // Is there an approved option to read, or a Finalize entry the rebinding note compares with? Both sides read it; not every stage has one.
    needsOptions () {
      var c = this.rta.completion
      var sum = rtaStage.optionSummary(this.rta)

      return !!sum.approvedOptionNo || !!sum.approvedOptionId || !!sum.finalOptionNo || this.stored.optionId !== null || !!(c && c.optionId !== null && c.optionId !== undefined)
    }

  },


  watch: {

    // The server moved the decision (a command's answer, a re-read): the form follows, unless an edit is waiting to be saved.
    storedKey () {
      if (!this.busy()) {
        this.syncForm()
      }
    },

    // The form drives the e-mail button and the bottom button of the drawer: tell it what is on screen.
    'form.status' () {
      this.announceDraft()
    },

    'form.remarks' () {
      this.announceDraft()
    },

    needsOptions (now) {
      if (now) {
        this.loadOptions()
      }
    },

    // The batch is still going out: read the RTA until it has settled; once it has (or the line is gone), stop.
    mailInFlight (now) {
      if (now) {
        this.pollMail()
      } else {
        this.stopMailPoll()
      }
    },

    // Another option is approved now (the Telco moved on, or an Unlock let it): the block and the names come from the list again.
    'rta.approvedOptionId' () {
      if (this.needsOptions) {
        this.loadOptions()
      }
    },

    // The stage moved (a re-read after a refusal, an Unlock): the options' statuses moved with it.
    'rta.status' () {
      if (this.needsOptions) {
        this.loadOptions()
      }
    }

  },


  methods: {

    /* ---------------------------------------------------------------------
       The form
    --------------------------------------------------------------------- */

    // Is anything of this form waiting to be saved, or being saved?
    busy () {
      return !!this.autosaveState && (this.autosaveState.pending || this.autosaveState.saving)
    },

    hasPending () {
      return this.busy()
    },

    // The last save was refused and nothing newer waits (XOL-67 B10): the screen holds a decision the server does not, so a command that
    // reads the server (Transition, Close, Lock, Unlock) must not go on until a later save succeeds.
    isRefused () {
      return this.autosave.refused()
    },

    // Put the server's decision on screen.
    syncForm () {
      var tc = this.stored
      var status = tc.approvedStatus || 'Pending'

      var date = status === 'Pending' ? null : (tc.approved_at ? decisionDay(tc.approved_at) : appNowDateSql())
      var remarks = tc.remarks || ''

      if (this.form.status !== status) {
        this.form.status = status
      }

      if (this.form.date !== date) {
        this.form.date = date
      }

      if (this.form.remarks !== remarks) {
        this.form.remarks = remarks
      }

      this.base = tc.approvedStatus || null
      this.dateTouched = false

      // The form shows what the server holds now: a refusal of an earlier edit is moot.
      if (this.autosave && this.autosave.refused()) {
        this.autosave.cancel()
      }
    },

    onStatus (value) {
      // Approve only (decision 142): the select offers Pending and Approved, and nothing else is taken (a Rejected would be a 422 `invalid_value`).
      if (!this.editable || value === this.form.status || rtaDecision.DECISION_STATUSES.indexOf(value) === -1) {
        return
      }

      this.form.status = value

      // A new decision is dated today, as the server will stamp it (a date the user picked stays); Pending has no date. Going back to
      // what the server holds shows its date.
      if (value === 'Pending') {
        this.form.date = null
        this.dateTouched = false
      } else if (this.storedStatus === value && this.stored.approved_at) {
        this.form.date = decisionDay(this.stored.approved_at)
        this.dateTouched = false
      } else if (!(this.dateTouched && this.form.date)) {
        this.form.date = appNowDateSql()
      }

      this.queue()
    },

    onDate (value) {
      if (!this.editable || !value || value === this.form.date) {
        return
      }

      this.form.date = value
      this.dateTouched = true
      this.queue()
    },

    onRemarks (html) {
      if (!this.editable || html === this.form.remarks) {
        return
      }

      this.form.remarks = html
      this.queue()
    },

    values () {
      return { status: this.form.status, date: this.form.date, dateTouched: this.dateTouched, remarks: this.form.remarks }
    },

    queue () {
      this.autosave.changed(this.values())
    },

    // What the drawer's bottom button judges besides the stored decision: null once the form says what the server holds.
    announceDraft () {
      this.$emit('draft', this.isClient ? null : { status: this.form.status, remarks: this.form.remarks })
    },


    /* ---------------------------------------------------------------------
       The auto-save: POST /actions/{id}/rta/decision (API E14)
    --------------------------------------------------------------------- */

    decisionUrl () {
      return '/actions/' + this.rta.id + '/rta/decision'
    },

    // X-Edit-Session (API DEV-17): the drawer's form scope has one id per open drawer, so the saves of a session merge into one
    // Activity row.
    editHeaders () {
      var scope = this.requestSyncScope
      var session = scope && scope.editSession ? scope.editSession : null

      return session ? { 'X-Edit-Session': session } : null
    },

    // Resolves with the answer; REJECTS with the result on a refusal (rtaAutosave reports it once).
    sendDecision (values) {
      return this.rtaCommand('post', this.decisionUrl(), rtaDecision.decisionBody(values, this.base), 'Decision', this.editHeaders()).then(function (result) {
        return result.ok ? result.data : Promise.reject(result)
      })
    },

    // The server took the decision: the drawer follows the answer's `action`.
    saved (data, values) {
      var action = data && data.action && typeof data.action === 'object' ? data.action : null
      var tc = action && action.state && action.state.rta && action.state.rta.tc ? action.state.rta.tc : null

      // The drawer follows the answer even when this form has gone (a save that was still waiting when the step changed).
      this.rtaAnnounce(data, this.rta.id)

      if (this._isDestroyed) {
        return
      }

      this.base = tc ? (tc.approvedStatus || null) : (values.status === 'Pending' ? null : values.status)

      if (values.dateTouched && this.form.date === values.date) {
        this.dateTouched = false
      }
    },

    // A refused save keeps what the user typed, says why once, and, when the server's decision is not the one the screen was
    // built on, drops the edit and shows the server's.
    refusedSave (result) {
      if (!result || typeof result !== 'object' || !('ok' in result)) {
        return
      }

      var failure = result.failure || {}
      var notice = this.rtaRefuse(result, {
        telco: this.names.telco,
        towerCo: this.names.towerCo,
        role: rtaStage.isTcCloser(this.rta, this.viewerOrg, this.viewerProfile) ? 'tcc' : 'tce',
        command: 'decision',
        optionNo: this.approvedOptionNo,
        changedOptionNo: this.approvedOptionNo,
        changedByParty: rtaStage.optionSummary(this.rta).approvedChangedByParty
      })

      if (STALE.indexOf(failure.code) !== -1 || (notice && notice.reread)) {
        this.autosave.cancel()
        this.reread()
      }
    },

    // The page is being left: what waits goes out as a fetch keepalive request, with the auth headers (DEV-18).
    // With a save in flight and newer values waiting (`ctx.after` = the running save's values), the base is what that save will make the
    // server hold; with the same values again it is the base the running save used (an identical decision is a replay, 200).
    keepaliveDecision (values, ctx) {
      var after = ctx && ctx.after ? ctx.after : null
      var base = after ? (after.status === 'Approved' ? after.status : null) : this.base

      return keepalivePost(this.getEndpoint(this.decisionUrl()), rtaDecision.decisionBody(values, base), this.editHeaders())
    },

    // Read the RTA again every few seconds while the decision e-mail is "Sending" (XOL-67 B10). The answer reaches the drawer through
    // `rta-changed`, so `stored.decisionMail` changes and `mailInFlight` ends the loop by itself.
    pollMail () {
      var self = this

      // One chain at a time: a restart (the batch went out again) retires the one running, even if its read is still in flight.
      this.stopMailPoll()

      var chain = this.mailChain

      this.mailPolls = 0

      var alive = function () {
        return chain === self.mailChain && !self._isDestroyed && self.mailInFlight && self.mailPolls < MAIL_POLL_MAX
      }

      var tick = function () {
        self.mailTimer = null

        if (!alive()) {
          return
        }

        self.mailPolls++

        self.refetchActionResult(self.rta.id).then(function () {
          if (alive()) {
            self.mailTimer = setTimeout(tick, MAIL_POLL_MS)
          }
        })
      }

      this.mailTimer = setTimeout(tick, MAIL_POLL_MS)
    },

    stopMailPoll () {
      this.mailChain++

      if (this.mailTimer) {
        clearTimeout(this.mailTimer)
        this.mailTimer = null
      }
    },

    // The edit is gone; show what the server holds.
    reread () {
      var self = this

      return this.refetchActionResult(this.rta.id).then(function () {
        if (!self._isDestroyed) {
          self.syncForm()
        }
      })
    },

    // RequestMixins' own re-read adopts through requestRowSync (a step shape). An RTA's is the drawer's: GET it, tell the drawer.
    refetchActionResult (actionId) {
      var self = this

      if (!actionId) {
        return Promise.resolve({ data: null, gone: false })
      }

      return axios.get(this.getEndpoint('/actions/' + actionId)).then(function (response) {
        var data = response.data && response.data.data ? response.data.data : response.data

        if (data && typeof data === 'object') {
          self.rtaAnnounce({ action: data }, actionId)
        }

        return { data: data || null, gone: false }
      }).catch(function (e) {
        console.error('XOL-67: re-read of RTA', actionId, 'failed', e)

        return { data: null, gone: !!(e && e.response && e.response.status === 404) }
      })
    },

    /**
     Save what waits, now. Resolves { ok }: false when the save was refused (its notice is up and the close or the command must
     not go on). The RTA is read from `rtaData` afterwards, which the save has already updated.
     */
    flush () {
      if (this.autosave.refused()) {
        return this.answerRefusal()
      }

      return this.autosave.flush().then(function (r) {
        return { ok: !!r.ok }
      })
    },

    // A command (Transition, Close, Lock, Unlock, Send e-Mail) was stopped by the last save's refusal (XOL-67 B10) and must say why. A 422
    // is said again in its words. Any other refusal (a 5xx, a network error) says nothing about the decision: it is sent once more, and the
    // command goes on if the server takes it.
    answerRefusal () {
      var result = this.autosave.lastRefusal()
      var failure = result && result.failure ? result.failure : null

      if (failure && failure.status === 422) {
        this.refusedSave(result)

        return Promise.resolve({ ok: false })
      }

      return this.autosave.retry().then(function (r) {
        return { ok: !!r.ok }
      })
    },


    /* ---------------------------------------------------------------------
       Send / Re-send {status} e-Mail (decision 55): RequestMixins' flow, for the event `rta_decision`
    --------------------------------------------------------------------- */

    sendDecisionEmail () {
      var self = this

      if (!this.emailView.enabled) {
        return Promise.resolve(null)
      }

      // A 409 invalid_state (the decision is not an Approved one, C11: the backstop of the button that is not drawn for Pending) is toasted by notifyOutcome.
      return this.sendApprovalEmail(this.mailRow, {
        event: 'rta_decision',
        flush: function () {
          return self.flush().then(function (r) { return r.ok })
        }
      })
    },


    /* ---------------------------------------------------------------------
       Decline… the approved option (E9, decision 142; UX spec 4.6)
    --------------------------------------------------------------------- */

    // Flush the decision form first (a pending edit would be cleared by the Decline anyway, but a refused one must say why), then ask for the reason.
    startDecline () {
      var self = this

      if (this.declining || this.declineOpen || !this.canDecline) {
        return Promise.resolve(null)
      }

      return this.flush().then(function (r) {
        if (!r.ok || self._isDestroyed || !self.canDecline) {
          return null
        }

        self.declineReason = ''
        self.declineError = ''
        self.declineOpen = true

        return true
      })
    },

    cancelDecline () {
      if (this.declining) {
        return
      }

      this.declineOpen = false
      this.focusDeclineButton()
    },

    onDeclineReason (value) {
      this.declineReason = value

      if (String(value || '').trim() !== '') {
        this.declineError = ''
      }
    },

    focusDeclineButton () {
      var self = this

      this.$nextTick(function () {
        var b = self.$refs.declineButton

        if (b && b.$el && typeof b.$el.focus === 'function') {
          b.$el.focus()
        }
      })
    },

    // POST /candidate/{id}/decline { reason }. The answer carries the RTA ({ action, option, decisionReset, notice }): the drawer follows it to the step it is
    // in now (Client Review: Step 3; Searching: Step 2). A refusal is said in the words of its code and the RTA is read again.
    doDecline () {
      var self = this
      var reason = String(this.declineReason || '').trim()
      var option = this.boundOption

      if (this.declining || !this.declineOpen || !option) {
        return Promise.resolve(null)
      }

      // The reason is required: said under the field, nothing sent, focus back in it.
      if (!reason) {
        this.declineError = this.declineCopy.reasonRequired
        this.$nextTick(function () {
          var field = typeof document !== 'undefined' ? document.querySelector('.rta-decline-field textarea') : null

          if (field && typeof field.focus === 'function') {
            field.focus()
          }
        })

        return Promise.resolve(null)
      }

      this.declining = true

      var optionNo = Number(option.optionNo)

      return this.rtaCommand('post', '/candidate/' + option.id + '/decline', { reason: reason }, 'Option ' + optionNo).then(function (result) {
        self.declining = false

        if (self._isDestroyed) {
          return result
        }

        if (!result.ok) {
          // The server wants a reason too (a blank one slipped through): it is said where the user types it.
          if (result.failure && result.failure.code === 'reason_required') {
            self.declineError = self.declineCopy.reasonRequired

            return result
          }

          self.declineOpen = false
          self.refusedCommand(result, 'decline', optionNo)
          self.focusDeclineButton()

          return result
        }

        var data = result.data || {}
        var action = data.action && typeof data.action === 'object' ? data.action : null
        var to = action && action.status ? (action.status === 'Searching' ? 'Searching' : 'Client Review') : self.declineToStage

        self.declineOpen = false
        self.rtaToast(rtaDecision.sentence('decline', { optionNo: optionNo, toStage: to }), data, { actor: 'towerco', orgs: [self.names.telco], towerCo: self.names.towerCo })

        // The drawer takes the RTA (its stage, the counts, the stepper), then goes to the step the RTA is in now.
        self.rtaAnnounce(data, self.rta.id)
        self.$emit('declined', { stage: to, optionNo: optionNo })

        return result
      })
    },

    // A refused Decline: the toast in the words of its code (not_approved: a review reversed the option meanwhile; wrong_phase; decision_locked ...), then the RTA and
    // the options are read again so the screen shows what the server holds.
    refusedCommand (result, command, optionNo) {
      var notice = this.rtaRefuse(result, {
        telco: this.names.telco,
        towerCo: this.names.towerCo,
        role: 'tcc',
        command: command,
        optionNo: optionNo,
        changedOptionNo: this.approvedOptionNo,
        changedByParty: rtaStage.optionSummary(this.rta).approvedChangedByParty
      })
      var failure = result && result.failure ? result.failure : {}

      if (failure.status === 409 || (notice && notice.reread)) {
        this.reread()
        this.loadOptions()
      }

      return notice
    },

    /* ---------------------------------------------------------------------
       The options (the approved option's block, the names and the rebinding note)
    --------------------------------------------------------------------- */

    loadOptions () {
      var self = this
      var seq = ++this.optionsSeq

      if (!this.rta || !this.rta.id) {
        return Promise.resolve([])
      }

      return axios.get(this.getEndpoint('/candidates/rta/' + this.rta.id + '?per_page=50')).then(function (response) {
        var body = response ? response.data : null
        var rows = Array.isArray(body) ? body : (body && Array.isArray(body.data) ? body.data : [])

        if (seq === self.optionsSeq && !self._isDestroyed) {
          self.options = rows
        }

        return self.options
      }, function (e) {
        console.error('XOL-67: could not read the options of RTA', self.rta.id, e)

        return []
      })
    },

    /* ---------------------------------------------------------------------
       Focus
    --------------------------------------------------------------------- */

    // After a Lock or an Unlock: the control the user's attention goes to (RequestMixins.focusLockLine).
    focusLine (which) {
      this.focusLockLine(which)
    },

    // The Decision select's input: its name ("Decision") and the notes it points at. Element re-renders the input and Vue's patch
    // takes attributes it did not put there off again, so they are put back whenever they change (as SelectOrganisation does).
    applySelectAttrs () {
      var s = this.$refs.statusSelect
      var input = s && s.$el && typeof s.$el.querySelector === 'function' ? s.$el.querySelector('input') : null

      if (!input) {
        return
      }

      var set = function (name, value) {
        if (value === null || value === undefined) {
          if (input.hasAttribute(name)) {
            input.removeAttribute(name)
          }
        } else if (input.getAttribute(name) !== value) {
          input.setAttribute(name, value)
        }
      }

      set('aria-label', 'Decision')
      set('aria-describedby', this.selectDescribedBy)

      if (this.watchedInput !== input && typeof MutationObserver !== 'undefined') {
        this.unwatchSelectInput()
        this.watchedInput = input
        this.attrObserver = new MutationObserver(this.applySelectAttrs)
        this.attrObserver.observe(input, { attributes: true, attributeFilter: ['aria-label', 'aria-describedby'] })
      }
    },

    unwatchSelectInput () {
      if (this.attrObserver) {
        this.attrObserver.disconnect()
      }

      this.attrObserver = null
      this.watchedInput = null
    }

  },


  created () {
    var self = this

    // Not reactive: the auto-save of the decision. Its `state` is.
    this.autosave = createAutosave({
      send: function (values) { return self.sendDecision(values) },
      onSaved: function (data, values) { self.saved(data, values) },
      onRefused: function (result) { self.refusedSave(result) },
      keepalive: function (values, ctx) { return self.keepaliveDecision(values, ctx) },
      keepaliveWhileSaving: true
    })
    this.autosaveState = this.autosave.state
    this.optionsSeq = 0
    this.mailTimer = null
    this.mailPolls = 0
    this.mailChain = 0
    this.attrObserver = null
    this.watchedInput = null

    this.syncForm()
  },


  mounted () {
    if (this.needsOptions) {
      this.loadOptions()
    }

    this.announceDraft()
    this.applySelectAttrs()

    if (this.mailInFlight) {
      this.pollMail()
    }
  },


  updated () {
    this.applySelectAttrs()
  },


  beforeDestroy () {
    var save = this.autosave

    // The safety flush: whatever waits is saved now, and the card stops hearing from this form.
    save.flush().then(function () {
      save.destroy()
    })

    this.optionsSeq++
    this.stopMailPoll()
    this.unwatchSelectInput()
    this.$emit('draft', null)
  }

}

</script>


<style scoped>
  .rta-decision__title { margin-top: 16px; }
  .rta-decision__select { width: 200px; }
  .rta-decision__heading { margin: 0 12px 0 0; display: inline-block; }
  .rta-decision__client-line { margin: 8px 0 0; }
  .rta-note { display: inline-block; margin-top: 4px; line-height: 1.45; }

  /* The inline form's rows: the remarks and the e-mail button take a row of their own (step5.vue). */
  .el-form--inline .rta-decision__full { display: block; width: 100%; }
  .el-form--inline .rta-decision__full >>> .el-form-item__content { display: block; }
  .rta-decision__email { padding-top: 1em; }

  /* The tooltip wrapper of the Send / Re-send button (step2.vue, step5.vue). */
  .approval-send-wrap { display: inline-block; }
  .approval-send-wrap.is-mail-off,
  .approval-send-wrap.is-mail-off >>> .el-button { cursor: not-allowed; }

  @media (max-width: 767px) {
    .rta-decision__select { width: 100%; }
    .el-form--inline .rta-decision__form >>> .el-form-item { display: block; }
  }
</style>
